/**
 * CompanyLogo — 기업 로고 한 칸.
 *
 * 주소는 딥서치 CDN 에서 종목 심볼로 바로 만든다(lib/companyLogo.ts). API 호출이
 * 없으므로 목록 어디에나 붙일 수 있다.
 *
 * ⚠️ 없는 로고는 404 가 아니라 **403** 이다 — 미리 확인할 방법이 없어서 폴백이 전부다:
 *   ① 주소를 만들 수 없는 심볼(빈 값·기업명·비정형) → 아예 요청하지 않는다
 *   ② 요청했는데 403 → onError 로 이니셜 배지
 *   ③ 한 번 실패한 주소는 lib/companyLogo 의 캐시에 남겨, 리렌더·재마운트마다 403 을
 *      다시 부르지 않는다. 표 한 장에 수십 행이 뜨는 화면이라 이게 없으면 시끄럽다
 *      (바닐라 차트 위젯도 같은 캐시를 쓴다)
 *
 * 흰 타일 위에 얹는 이유: 국내 로고는 투명 배경 PNG 라 다크 테마에서 어두운 로고가
 * 배경에 묻힌다. 파비콘처럼 타일을 깔면 세 테마에서 같은 모양이 된다.
 *
 * 로고는 늘 기업명 옆에 놓이는 장식이다 — 그래서 alt 는 비우고 화면 낭독기에서 감춘다.
 * (이름을 두 번 읽어주는 게 이득이 없다)
 */
import { useState } from "react";
import { logoFailed, logoInitial, logoUrl, markLogoFailed } from "../lib/companyLogo";

// 크기는 '옆 글자에 맞춘다'가 기본이지만, **로고를 알아볼 수 있는 최소 크기**가 먼저다.
// 14~16px 은 파비콘 크기라 기업이 구분되지 않는다 — 로고가 정보로서 일하는 자리
// (시장 이슈 관련 기업처럼 기업을 훑어보는 목록)에는 lg 를 쓴다.
export type CompanyLogoSize =
  | "xs"   // 14px — 알약 칩 안, 이름이 주인공인 곳 (peer-chip)
  | "sm"   // 16px — 조밀한 목록·표 행 (드롭다운·자동완성·결과표·분석 대상)
  | "md"   // 20px — 이름이 제목처럼 서는 카드·칩 (버크셔 보유 종목 · 시장 이슈 목록 칩)
  | "lg";  // 32px — 로고로 기업을 알아보는 자리 (시장 이슈 관련 기업)

export interface CompanyLogoProps {
  /** 종목 심볼 — "005930" · "KRX:005930" · "AAPL" · "NASDAQ:AAPL" 다 받는다 */
  symbol?: string | null;
  /** 기업명 — 폴백 배지의 글자로 쓴다 */
  name?: string | null;
  size?: CompanyLogoSize;
  className?: string;
}

export function CompanyLogo({ symbol, name, size = "sm", className }: CompanyLogoProps) {
  const url = logoUrl(symbol);
  // 이 주소로 403 을 받았다는 기록. url 이 바뀌면 자동으로 다시 시도된다
  // (같은 컴포넌트가 다른 기업으로 재사용되는 목록에서 필요하다)
  const [brokenUrl, setBrokenUrl] = useState<string | null>(null);

  const cls = ["co-logo", `co-logo-${size}`, className].filter(Boolean).join(" ");

  if (!url || logoFailed(url) || brokenUrl === url) {
    return (
      <span className={`${cls} co-logo-fb`} aria-hidden="true">
        {logoInitial(name, symbol)}
      </span>
    );
  }

  return (
    <img
      className={cls}
      src={url}
      alt=""
      aria-hidden="true"
      loading="lazy"
      decoding="async"
      onError={() => {
        markLogoFailed(url);
        setBrokenUrl(url);
      }}
    />
  );
}
